import { Col, Row } from "reactstrap";
import { useCallback, useMemo } from "react";
import {
  IFinanceInfo,
  ModalType,
} from "../../../../../Types/UserProfileDetails";
import { financialInformationModal } from "../../../../../utils/Constant";
import { useAppDispatch } from "../../../../../ReduxToolkit/Hooks";
import { openModal } from "../../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import EmployeeInfoCard from "../../EmployeeInfoCard";
import { secureFieldFormatter } from "../../../../../CommonElements/SecureFieldFormatter";
import { findAndCategorizedAttachment } from "../../../../../utils/fileUtils";
import { setAttachment } from "../../../../../ReduxToolkit/Slice/AttachmentSlice";
import { useEmployeeProfile } from "../../../../../hooks/useEmployeeProfile";
import { canShowEditInfo, hasAccessPrivateFields } from "../../../utils/access";
import { PRIVILEGED_IDS } from "../../../utils/constant";

const norm = (v?: string | null) => (v ?? "").trim().toLowerCase();

const FIELD_ORDER: Array<keyof IFinanceInfo | string> = [
  "account_holder_name",
  "bank_name",
  "account_number",
  "ifsc_code",
  "account_type",
  "bank_proof",
  "registered_email",
  "registered_mobile",
];

const OMIT_KEYS: Array<keyof IFinanceInfo | string> = [
  "id",
  "user_id",
  "created_at",
  "updated_at",
];

const PREVIEW_MODAL_BY_KEY: Record<string, ModalType> = {
  aadharCard: "aadharCardModal",
  panCard: "panCardModal",
  bank_proof: "bankProofModal",
};

const EmployeeFinancialInfo = () => {
  const dispatch = useAppDispatch();
  const { employeeProfileDetails, loggedInEmployee, loggedInEmployeeRole } =
    useEmployeeProfile();

  const employeeFinancialInfo =
    employeeProfileDetails?.employeeFinancialInformation ?? [];

  const employeeId = Number(employeeProfileDetails?.jobInformation?.userId);

  const viewerId = Number(loggedInEmployee?.id);

  const viewerRole = ["hr", "manager"].includes(loggedInEmployeeRole);

  const canEdit = useMemo(() => {
    if (!employeeProfileDetails || !loggedInEmployee) return false;
    return canShowEditInfo(employeeProfileDetails, loggedInEmployee);
  }, [employeeProfileDetails, loggedInEmployee]);

  // View privilege for private fields (mask/preview)
  const hasAccess = useMemo(() => {
    if (!employeeId || !viewerId || !loggedInEmployee?.user_type) return false;
    return hasAccessPrivateFields(
      employeeId,
      viewerId,
      loggedInEmployee.user_type
    );
  }, [employeeId, viewerId, loggedInEmployee]);

  const handleOpenModal = useCallback(
    (modalType: ModalType, selectedId?: number) => {
      dispatch(openModal({ modalType, selectedId }));
    },
    [dispatch]
  );

  const formatters = useMemo(() => {
    if (!hasAccess) return {};
    const f = secureFieldFormatter<any>();
    return {
      account_number: f,
      bank_proof: f,
      ifsc_code: f,
      registered_mobile: f,
      registered_email: f,
      account_type: f,
      aadharCardNumber: f,
      panCardNumber: f,
    } as const;
  }, [hasAccess]);

  const omitKeys = useMemo(
    () =>
      viewerRole && !PRIVILEGED_IDS.includes(viewerId as any)
        ? [
            ...OMIT_KEYS,
            "panCard",
            "aadharCard",
            "panCardNumber",
            "aadharCardNumber",
          ]
        : OMIT_KEYS,
    [viewerRole,viewerId]
  );

  const previewableKeys = useMemo(
    () => (hasAccess ? (["bank_proof", "aadharCard", "panCard"] as const) : []),
    [hasAccess]
  );

  const handlePreview = useCallback(
    (key: string, value: unknown) => {
      if (typeof value !== "string") return;

      const file = findAndCategorizedAttachment(value);
      if (!file) return;

      const modalType = PREVIEW_MODAL_BY_KEY[key] ?? "bankProofModal";
      dispatch(setAttachment(file));
      dispatch(openModal({ modalType }));
    },
    [dispatch]
  );

  if (!employeeFinancialInfo.length) {
    return <Row />; // nothing to show
  }

  return (
    <Row>
      {employeeFinancialInfo.map((financial) => (
        <Col
          md="6"
          key={
            financial.id ?? `${financial.bank_name}-${financial.account_number}`
          }
          className="mb-3"
        >
          <EmployeeInfoCard<IFinanceInfo>
            title="Financial Information"
            entity={financial}
            showEditInfo={canEdit}
            canEdit={canEdit}
            bodyClassName="p-3"
            fieldOrder={FIELD_ORDER}
            omitKeys={omitKeys}
            iconOnClick={() =>
              handleOpenModal(
                financialInformationModal,
                financial.id ?? undefined
              )
            }
            formatters={formatters as any}
            previewableKeys={[...previewableKeys]}
            onPreview={handlePreview}
          />
        </Col>
      ))}
    </Row>
  );
};

export default EmployeeFinancialInfo;
